import useRequest from '@ahooksjs/use-request';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { uiHelpers } from '../../helpers';
import { usePreferences } from '../../hooks/usePreferences';
import { Category } from '../../py-generated/datalouder/stock/types';
import { dailypriceService } from '../../services';
import { Region } from '../../shared/types';
import { ReactTable } from '../../types';
import {
  ccassShortcutColumn,
  changeColumnV2,
  changePercentageColumnV2,
  concentrationColumn,
  relPriceColumnV2,
  relVolumnColumnV2,
  totalNoColumnV2,
} from '../table/columns';
import DLStockPeek from '../table/DLStockPeek';
import DLTable, { DESKTOP_ONLY_CLASS_NAME } from '../table/DLTable/DLTable';
import {
  ConcentrationFilterWithoutSlider,
  NumberFilter,
  ParticipantNumChangeFilterWithoutSlider,
  PriceChangeFilter,
  PriceFilter,
  PriceRangeFilterWithoutSlider,
} from '../table/filters';

interface Props {
  region: Region;
  ticker: string;
  category: Category;
}
const STOCK_TEMP_PRICE_ID = 'stock-temp-price';
export const STOCK_TEMP_TURONVER_ID = 'stock-temp-turnover';
export const STOCK_TEMP_MKTCAP_ID = 'stock-temp-mktcap';
const StockTemporalTable: React.FC<Props> = ({ region, ticker, category }) => {
  const {
    avgVolInterval,
    participantNumChangeInterval,
    priceChangeInterval,
    priceRangeInterval,
    topN,
  } = usePreferences();
  const { t, i18n } = useTranslation();

  const { data, loading } = useRequest(
    dailypriceService.statisticsHistoryOptions(category, ticker)
  );
  // onValueChange={value =>
  //   dispatch(preferenceActions.setAvgVolInterval(value))
  // }
  let columns: ReactTable.Column[] = [
    {
      Header: t('Date'),
      id: 'stock-temp-date',
      accessor: 'd',
      width: 90,
      Cell: ({ cell: { value } }) => (
        <div>{uiHelpers.formatDateLocal(value)}</div>
      ),
      sortDescFirst: true,
      disableFilters: true,
    },
    {
      id: STOCK_TEMP_PRICE_ID,
      Header: t('Price'),
      accessor: item => item['ref']['price'],
      width: 70,
      Filter: PriceFilter,
      Cell: ({ cell: { value } }) => <div>{uiHelpers.formatMoney(value)}</div>,
      sortDescFirst: true,
    },
    changeColumnV2(t, priceChangeInterval, PriceChangeFilter),
    changePercentageColumnV2(t, priceChangeInterval),
    relVolumnColumnV2(t, avgVolInterval),
    {
      id: STOCK_TEMP_TURONVER_ID,
      Header: t('Turnover'),
      accessor: item => item.ref.turnover,
      width: 100,
      Cell: ({ cell: { value } }) => (
        <div>{uiHelpers.formatLargeMoney(i18n, value)}</div>
      ),
      Filter: NumberFilter,
      sortDescFirst: true,
      className: DESKTOP_ONLY_CLASS_NAME,
    },
    {
      id: STOCK_TEMP_MKTCAP_ID,
      Header: t('Mktcap'),
      accessor: item => item.ref.mktcap,
      width: 100,
      Cell: ({ cell: { value } }) => (
        <div>{uiHelpers.formatLargeMoney(i18n, value)}</div>
      ),
      sortDescFirst: true,
      Filter: NumberFilter,
      className: DESKTOP_ONLY_CLASS_NAME,
    },
    totalNoColumnV2(
      t,
      participantNumChangeInterval,
      ParticipantNumChangeFilterWithoutSlider
    ),
    concentrationColumn(t, topN, ConcentrationFilterWithoutSlider),
    relPriceColumnV2(t, priceRangeInterval, PriceRangeFilterWithoutSlider),
    ccassShortcutColumn(),
  ];
  if (region !== 'hk') columns = columns.filter(({ hkOnly }) => !hkOnly);
  if (region !== 'us') columns = columns.filter(({ usOnly }) => !usOnly);

  return (
    <div>
      <DLTable
        tableId="stock-temporal"
        columns={columns}
        data={data || []}
        renderRowSubComponent={({
          mode,
          row: {
            original: { d },
          },
        }) => {
          return (
            <DLStockPeek
              stockCode={ticker}
              mode={mode}
              region={region}
              when={d}
            />
          );
        }}
        defaultSortBy={[
          {
            id: 'stock-temp-date',
            desc: true,
          },
        ]}
        defaultPageSize={10}
        loading={loading}
      />
      {region === 'hk' && (
        <p>
          <small>{t('* All CCASS data is 3 trading days old.')}</small>
        </p>
      )}
    </div>
  );
};

export default StockTemporalTable;
